import { TemplateResult, html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { SlotController } from '../../controllers/slot-controller';
import { BlocksElement } from '../BlocksElement';
import type { CardVariant } from './card.model';
import styles from './card.styles';

/**
 * @slot header - The component header content
 * @slot default - The component default content
 * @slot footer - The component footer content
 * @csspart base - The component's base wrapper.
 * @csspart header-wrapper - The component's header wrapper.
 * @csspart part-header - The component's header slot.
 * @csspart body - The component's body content.
 * @csspart part-default - The component's default slot.
 * @csspart footer-wrapper - The component's footer wrapper.
 * @csspart part-footer - The component's footer slot.
 */
export class BlocksCard extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Option to remove padding
   */
  @property({ type: Boolean, reflect: true })
  removePadding: boolean | undefined;

  /**
   * Href attribute
   */
  @property({ type: String, reflect: true })
  href: string | undefined;

  /**
   * Style variants
   * <cat-text-passage size="sm">
   * <ul>
   * <li>**bare** renders a card without a border or drop shadow</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String, reflect: true })
  variant: CardVariant;

  /**
   * Allow a card to be auto height
   */
  @property({ type: Boolean, reflect: true })
  isAutoHeight: boolean | undefined;

  /**
   * Set card body max-height for cards with long body copy that need the ability to scroll
   */
  @property({ type: String, reflect: true })
  bodyMaxHeight: string | undefined;

  /**
   * Aria label for accessibility
   * 1) This should contain a readout of the data the card is representing.
   */
  @property({ type: String, reflect: true })
  arialabel: string | undefined;

  /**
   * Provides a unique identifier to group the  header content with its
   * aria-labelledby attribute, enabling accessible announcements for screen readers.
   * This ID helps associate descriptive text with the button / interactive element for improved
   * accessibility by providing context to assistive technologies.
   */
  @property({ type: String }) headerWrapperId: string | undefined;

  /**
   * Provides a unique identifier to group the footer wrapper container with its
   * aria-labelledby attribute, enabling accessible announcements for screen readers.
   * This ID helps associate descriptive text with the button / interactive element for improved
   * accessibility by providing context to assistive technologies.
   */
  @property({ type: String }) footerWrapperId: string | undefined;

  /**
   * Provides a unique identifier to group the body wrapper container with its
   * aria-labelledby attribute, enabling accessible announcements for screen readers.
   * This ID helps associate descriptive text with the button / interactive element for improved
   * accessibility by providing context to assistive technologies.
   */
  @property({ type: String }) bodyWrapperId: string | undefined;

  protected slots = new SlotController(this, 'header', null, 'footer');

  /**
   * Returns ids of associated element
   */
  private get ariaLabelledbyIds(): string {
    return [this.headerWrapperId, this.footerWrapperId, this.bodyWrapperId].filter(Boolean).join(' ');
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-card', {
      'cat-c-card--bare': this.variant === 'bare',
      'cat-c-card--remove-padding': this.removePadding,
      'cat-c-card--has-header-slot': this.slots.hasSlotted('header'),
      'cat-c-card--has-footer-slot': this.slots.hasSlotted('footer')
    });

    if (this.href) {
      return html`
        <a
          part="base"
          aria-label=${ifDefined(this.arialabel)}
          href=${this.href}
          class="${componentClassNames}"
          aria-labelledby=${this.ariaLabelledbyIds}
          style="${ifDefined(this.isAutoHeight ? 'height: auto;' : undefined)}"
        >
          <div part="header-wrapper" id=${ifDefined(this.headerWrapperId)} class="cat-c-card__header">
            <slot part="part-header" name="header"></slot>
          </div>

          <div
            part="body"
            class="cat-c-card__body"
            style="${ifDefined(
              this.bodyMaxHeight ? '--cat-text-passage-overflow: auto; --cat-text-passage-max-height: ' + this.bodyMaxHeight + ';' : undefined
            )}"
            id=${ifDefined(this.bodyWrapperId)}
          >
            <slot part="part-default"></slot>
          </div>

          <div part="footer-wrapper" id=${ifDefined(this.footerWrapperId)} class="cat-c-card__footer">
            <slot part="part-footer" name="footer"></slot>
          </div>
        </a>
      `;
    } else {
      return html`
        <div
          part="base"
          aria-labelledby=${this.ariaLabelledbyIds}
          class="${componentClassNames}"
          style="${ifDefined(this.isAutoHeight ? 'height: auto;' : undefined)}"
          role="region"
          aria-label=${ifDefined(this.arialabel)}
        >
          <div part="header-wrapper" id=${ifDefined(this.headerWrapperId)} class="cat-c-card__header">
            <slot part="part-header" name="header"></slot>
          </div>
          <div
            part="body"
            class="cat-c-card__body"
            style="${ifDefined(
              this.bodyMaxHeight ? '--cat-text-passage-overflow: auto; --cat-text-passage-max-height: ' + this.bodyMaxHeight + ';' : undefined
            )}"
            id=${ifDefined(this.bodyWrapperId)}
          >
            <slot part="part-default"></slot>
          </div>

          <div part="footer-wrapper" id=${ifDefined(this.footerWrapperId)} class="cat-c-card__footer">
            <slot part="part-footer" name="footer"></slot>
          </div>
        </div>
      `;
    }
  }
}

if (customElements.get('cat-card') === undefined) {
  customElements.define('cat-card', BlocksCard);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-card': BlocksCard;
  }
}
